import React from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';

import { Recipe } from '@/lib/recipes/types';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { getRecipeImageSource } from '@/constants/recipePlaceholders';

export interface RecipeCardProps {
  recipe: Recipe;
  onPress?: () => void;
  columnCount?: number;
  testID?: string;
}

export function RecipeCard({ recipe, onPress, columnCount = 1, testID }: RecipeCardProps) {
  const imageSource = getRecipeImageSource(recipe.images ?? []);
  const showImageSection = !!imageSource || columnCount !== 1;
  const totalTime = recipe.prepTime + recipe.cookTime;
  const showTimePill = totalTime > 0;

  return (
    <TouchableOpacity testID={testID} onPress={onPress} activeOpacity={0.95}>
      <View style={styles.recipeCard}>
        {showImageSection && (
          <View style={styles.imageContainer}>
            {imageSource ? (
              <Image source={imageSource} style={styles.recipeImage} resizeMode="cover" />
            ) : (
              <View style={styles.recipeImagePlaceholder}>
                <Ionicons name="restaurant-outline" size={40} color={Neutrals.textMuted} />
              </View>
            )}
            <LinearGradient
              colors={['transparent', 'rgba(0,0,0,0.6)']}
              style={styles.imageGradient}
            />
            <View style={styles.recipeStats}>
              {showTimePill && (
                <View style={styles.statBadge}>
                  <Ionicons name="time-outline" size={14} color={Brand.primary} />
                  <Text style={styles.statText}>{totalTime} min</Text>
                </View>
              )}
              <View style={styles.statBadge}>
                <Ionicons name="people-outline" size={14} color={Brand.primary} />
                <Text style={styles.statText}>{recipe.servings}</Text>
              </View>
            </View>
          </View>
        )}
        <View style={[styles.recipeHeader, !showImageSection && styles.recipeHeaderNoImage]}>
          {!showImageSection && (
            <View style={styles.recipeStatsInline}>
              {showTimePill && (
                <View style={styles.statBadge}>
                  <Ionicons name="time-outline" size={14} color={Brand.primary} />
                  <Text style={styles.statText}>{totalTime} min</Text>
                </View>
              )}
              <View style={styles.statBadge}>
                <Ionicons name="people-outline" size={14} color={Brand.primary} />
                <Text style={styles.statText}>{recipe.servings}</Text>
              </View>
            </View>
          )}
          <Text style={styles.recipeTitle} numberOfLines={2}>
            {recipe.title}
          </Text>
          {recipe.description && (
            <Text style={styles.recipeDescription} numberOfLines={2}>
              {recipe.description}
            </Text>
          )}
        </View>
        <View style={styles.recipeTags}>
          <View style={styles.tagsContainer}>
            {recipe.tags.slice(0, 3).map((tag) => (
              <View key={tag} style={styles.tag}>
                <Text style={styles.tagText}>{tag}</Text>
              </View>
            ))}
            {recipe.tags.length > 3 && (
              <View style={styles.tag}>
                <Text style={styles.tagText}>+{recipe.tags.length - 3}</Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  recipeCard: {
    backgroundColor: Neutrals.whiteFaded70,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: Neutrals.whiteFaded70,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  imageContainer: {
    width: '100%',
    aspectRatio: 4 / 3,
    backgroundColor: Neutrals.cardBgSolid,
    overflow: 'hidden',
  },
  recipeImage: {
    width: '100%',
    height: '100%',
  },
  recipeImagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: Neutrals.cardBgSolid,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
  },
  recipeStats: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    right: 12,
    flexDirection: 'row',
    gap: 8,
  },
  statBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Neutrals.whiteFaded90,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
  statText: {
    fontSize: 12,
    fontWeight: '600',
    color: Neutrals.textPrimary,
  },
  recipeHeader: {
    padding: 20,
    paddingBottom: 12,
  },
  recipeHeaderNoImage: {
    paddingTop: 20,
  },
  recipeStatsInline: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  recipeTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    lineHeight: 24,
  },
  recipeDescription: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    lineHeight: 20,
  },
  recipeTags: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    backgroundColor: primaryAlpha(0.2),
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: primaryAlpha(0.35),
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
    color: Brand.primaryDark,
  },
});

